import type { V1PlanInfo } from "schemas/billing";
import type { SubscriptionDetails } from "schemas/generated/billing";
import type { Workspace } from "schemas/generated/workspace";

import * as React from "react";

import {
  useCurrentWorkspace,
  useCurrentWorkspaceId,
} from "@editor/contexts/WorkspaceDashboardContext";
import { useIsWorkspaceOwner } from "@editor/hooks/useIsWorkspaceOwner";
import { useModal } from "@editor/hooks/useModal";
import { usePrefetchBillingInfoImmediately } from "@editor/hooks/usePrefetchImmediately";
import { billingRouter } from "@editor/trpc/billing";
import { subscriptionRouter } from "@editor/trpc/subscription";
import { workspaceRouter } from "@editor/trpc/workspace";

import Button from "@replo/design-system/components/button/Button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@replo/design-system/components/shadcn/core/card";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { Lock, Rocket } from "lucide-react";
import { Outlet } from "react-router-dom";
import { getV1BillingPlanInfoByTier } from "schemas/billing";

import BillingIntervalToggle from "../shared/BillingIntervalToggle";
import PlanCard from "../shared/PlanCard";
import { getStripePortalUrl } from "../shared/sharedBillingUtils";
import SubscriptonCancelledNotEndedAlert from "../shared/SubscriptonCancelledNotEndedAlert";
import V1BillingLimitsCard from "./V1BillingLimitsCard";

const V1Billing: React.FC<{
  activeSubscription?: SubscriptionDetails;
  isYearly: boolean;
  setIsYearly: (isYearly: boolean) => void;
}> = ({ activeSubscription, isYearly, setIsYearly }) => {
  const modal = useModal();

  const workspace = useCurrentWorkspace();
  const isWorkspaceOwner = useIsWorkspaceOwner(workspace?.id);
  const workspaceId = workspace?.id;

  usePrefetchBillingInfoImmediately();

  const showStripeConversion =
    isWorkspaceOwner &&
    activeSubscription?.paymentProcessor === "shopify" &&
    workspace?.preferredPaymentProcessor === "stripe";

  const { data: integrationUsage, isLoading: isLoadingIntegrationUsage } =
    workspaceRouter.getShopifyIntegrationsUsage.useQuery(
      workspaceId ?? skipToken,
    );
  const {
    data: publishedElementUsage,
    isLoading: isLoadingPublishedElementUsage,
  } = workspaceRouter.getPublishedElementsUsage.useQuery(
    workspaceId ?? skipToken,
  );

  const { data: subscriptionStatus } = subscriptionRouter.getStatus.useQuery(
    workspaceId &&
      activeSubscription?.paymentProcessor === "stripe" &&
      activeSubscription.paymentProcessorSubscriptionId
      ? {
          workspaceId,
          subscriptionId: activeSubscription.id,
        }
      : skipToken,
  );

  const { mutate: generateStripeConversionLink } =
    billingRouter.getStripeConversionLink.useMutation({
      onSuccess: (conversionUrl) => {
        window.location.href = conversionUrl;
      },
    });

  const planName = activeSubscription?.v1BillingTier ?? "free";

  const isLimitExceeded =
    publishedElementUsage &&
    hasReachedPublishingLimit(
      getV1BillingPlanInfoByTier(planName),
      publishedElementUsage,
    );
  const isLoading = isLoadingPublishedElementUsage || isLoadingIntegrationUsage;
  if (isLoading || !publishedElementUsage || !integrationUsage) {
    return null;
  }

  const shouldShowPublishLimitExceededMessage =
    isLimitExceeded && planName !== "custom" && planName !== "free";
  const shouldShowIntegrationLimitExceededMessage =
    planName !== "free" &&
    !(planName === "basic" && integrationUsage.current === 1) &&
    integrationUsage.current >=
      (integrationUsage.maximum ?? Number.POSITIVE_INFINITY);
  const shouldShowPaymentError = subscriptionStatus?.status === "past_due";

  let alertCard: React.ReactNode;
  if (!activeSubscription) {
  } else if (shouldShowPaymentError) {
    alertCard = (
      <BillingAlertCard
        title="Payment Method Error"
        description="There is an error with your payment method. Please update your payment method or your Subscription will be canceled."
      >
        <Button
          variant="primary"
          to={getStripePortalUrl(workspaceId ?? "")}
          size="base"
        >
          Update Payment Method
        </Button>
      </BillingAlertCard>
    );
  } else if (shouldShowPublishLimitExceededMessage) {
    alertCard = (
      <BillingAlertCard
        title="Publishing Limits Reached"
        description="You have reached the publishing limits on your current account. Upgrade to increase the publishing limits."
      >
        <Button
          variant="tertiary"
          size="base"
          to="https://support.replo.app/articles/22389707341325-What-is-the-best-Replo-plan-for-me"
        >
          Learn more
        </Button>
        <Button
          variant="primary"
          size="base"
          onClick={() =>
            modal.openModal({
              type: "billingModal",
              props: { source: "direct.billingDashboard" },
            })
          }
        >
          Upgrade
        </Button>
      </BillingAlertCard>
    );
  } else if (shouldShowIntegrationLimitExceededMessage) {
    alertCard = (
      <BillingAlertCard
        title="You have reached your limit"
        description="You have reached the Integration limit for this Workspace. Upgrade to increase your limits."
      >
        <Button
          variant="tertiary"
          size="base"
          to="https://support.replo.app/articles/22389707341325-What-is-the-best-Replo-plan-for-me"
        >
          Learn more
        </Button>
        <Button
          variant="primary"
          size="base"
          onClick={() =>
            modal.openModal({
              type: "billingModal",
              props: { source: "direct.billingDashboard" },
            })
          }
        >
          Upgrade
        </Button>
      </BillingAlertCard>
    );
  } else if (showStripeConversion) {
    alertCard = (
      <BillingAlertCard
        title="Update your payment method"
        description="We recently upgraded to Stripe as our preferred payment platform.
                    To take advantage of benefits such as multiple Shopify integrations within
                    a single workspace, please update your payment method."
      >
        <Button
          variant="primary"
          size="base"
          layoutClassName="mt-1"
          onClick={() => {
            if (!workspaceId) {
              return;
            }
            void generateStripeConversionLink({ workspaceId });
          }}
        >
          Update Payment Method
        </Button>
      </BillingAlertCard>
    );
  }

  if (!workspace) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {planName === "free" ? <UpgradeToPaidPlanCard /> : alertCard}
      <BillingInfo
        activeSubscription={activeSubscription ?? undefined}
        isWorkspaceOwner={isWorkspaceOwner}
        showStripeConversion={showStripeConversion}
        workspace={workspace}
        isYearly={isYearly}
        setIsYearly={setIsYearly}
      />

      <Outlet />
    </div>
  );
};

const hasReachedPublishingLimit = (
  planInfo: V1PlanInfo | undefined,
  publishedElements: {
    current: number;
    maximum: number | null;
  },
): boolean => {
  if (!planInfo) {
    return false;
  }
  if (publishedElements.current === 0 && planInfo.tier === "free") {
    return false;
  }
  return Boolean(
    planInfo.maxPublishedElements &&
      publishedElements.current >=
        (publishedElements.maximum ?? Number.POSITIVE_INFINITY),
  );
};

const BillingInfo = ({
  activeSubscription,
  isWorkspaceOwner,
  workspace,
  isYearly,
  setIsYearly,
}: {
  activeSubscription: SubscriptionDetails | undefined;
  isWorkspaceOwner: boolean;
  showStripeConversion: boolean;
  workspace: Workspace;
  isYearly: boolean;
  setIsYearly: (isYearly: boolean) => void;
}) => {
  const workspaceId = useCurrentWorkspaceId();

  const {
    data: nextSubscriptionBillingDate,
    isLoading: isLoadingNextSubscriptionBillingDate,
  } = billingRouter.getNextBillingDate.useQuery(
    workspaceId && activeSubscription?.id
      ? {
          workspaceId,
          subscriptionId: activeSubscription.id,
        }
      : skipToken,
  );

  const { data: integrationUsage, isLoading: isLoadingIntegrationUsage } =
    workspaceRouter.getShopifyIntegrationsUsage.useQuery(
      workspaceId ?? skipToken,
    );
  const {
    data: publishedElementUsage,
    isLoading: isLoadingPublishedElementUsage,
  } = workspaceRouter.getPublishedElementsUsage.useQuery(
    workspaceId ?? skipToken,
  );

  const isLoading =
    isLoadingPublishedElementUsage ||
    isLoadingIntegrationUsage ||
    isLoadingNextSubscriptionBillingDate;

  if (
    isLoading ||
    !publishedElementUsage ||
    !integrationUsage ||
    !workspaceId
  ) {
    return null;
  }

  return (
    <>
      <SubscriptonCancelledNotEndedAlert
        activeSubscription={activeSubscription}
        workspaceId={workspaceId}
        reploVersion="v1"
      />

      <div className="flex flex-col gap-4">
        {activeSubscription && (
          <V1BillingLimitsCard
            elementUsage={publishedElementUsage}
            integrationUsage={integrationUsage}
            activeSubscription={activeSubscription}
            nextBillDate={nextSubscriptionBillingDate ?? undefined}
            workspaceId={workspaceId ?? ""}
            isWorkspaceOwner={isWorkspaceOwner}
          />
        )}
        <BillingInfoCard
          activeSubscription={activeSubscription}
          workspace={workspace}
          isYearly={isYearly}
          setIsYearly={setIsYearly}
        />
      </div>
    </>
  );
};

type BillingInfoCardProps = {
  activeSubscription: SubscriptionDetails | undefined;
  workspace: Workspace;
  isYearly: boolean;
  setIsYearly: (isYearly: boolean) => void;
};

const BillingInfoCard: React.FC<BillingInfoCardProps> = ({
  activeSubscription,
  workspace,
  isYearly,
  setIsYearly,
}) => {
  const currentPlan = activeSubscription?.v1BillingTier ?? "free";

  // NOTE (Cole 2025-09-15): We don't show the custom plan if the workspace is Shopify billed - Shopify TOS thing
  const isCustomPlanVisible = workspace.preferredPaymentProcessor !== "shopify";

  const plans = [
    getV1BillingPlanInfoByTier("basic"),
    getV1BillingPlanInfoByTier("growth"),
    ...(isCustomPlanVisible ? [getV1BillingPlanInfoByTier("custom")] : []),
  ] as const;

  return (
    <Card className="flex-1 p-6">
      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <h3 className="text-2xl font-semibold">
            {currentPlan === "free" ? "Compare Plans" : "Manage Plans"}
          </h3>

          <BillingIntervalToggle
            isYearly={isYearly}
            setIsYearly={setIsYearly}
            isWorkspaceOnYearlySubscription={
              activeSubscription?.billingInterval === "yearly"
            }
          />
        </div>

        <div
          className={twMerge(
            "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4",
            isCustomPlanVisible && "xl:grid-cols-3",
          )}
        >
          {plans.map((plan) => (
            <PlanCard
              key={plan.tier}
              reploVersion="v1"
              plan={plan}
              currentPlan={currentPlan}
              isYearly={isYearly}
              workspace={workspace ?? undefined}
              source="direct.billingDashboardManage"
            />
          ))}
        </div>
      </div>
    </Card>
  );
};

type BillingAlertCardProps = {
  title: string;
  description: string;
  children: React.ReactNode;
};
const BillingAlertCard: React.FC<BillingAlertCardProps> = ({
  title,
  description,
  children,
}) => {
  return (
    <div className="bg-sheet border-border border-1 flex flex-row items-center justify-between gap-2 rounded-lg px-3 py-2">
      <div>
        <div className="header-h4">{title}</div>
        <div className="text-muted text-base">{description}</div>
      </div>
      <div className="flex flex-row gap-2">{children}</div>
    </div>
  );
};

const UpgradeToPaidPlanCard: React.FC = () => {
  const modal = useModal();

  return (
    <Card className="flex-1 gap-4 bg-default text-white p-2">
      <CardHeader className="p-2">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* eslint-disable-next-line replo/no-tailwind-literal-color */}
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100">
              <Lock className="text-default" size={18} />
            </div>
            <div className="flex flex-col gap-1">
              <CardTitle className="text-white">
                Upgrade to publish and optimize your Store
              </CardTitle>
              <CardDescription className="text-white">
                On the free plan, you are not able to publish any pages, track
                store performance or run A/B tests.
              </CardDescription>
            </div>
          </div>

          <Button
            variant="secondary"
            size="base"
            onClick={() => {
              modal.openModal({
                type: "billingModal",
                props: { source: "direct.billingDashboard" },
              });
            }}
          >
            <div className="flex items-center gap-2 text-default">
              <Rocket size={14} stroke="#1E293B" />
              Upgrade
            </div>
          </Button>
        </div>
      </CardHeader>
    </Card>
  );
};

export default V1Billing;
